import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, XCircle, ClipboardCheck } from "lucide-react";
import ClientShell from "./ClientShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { useFeedbackRequests, useCancelFeedbackRequest, useCreateFeedbackDraft } from "@/lib/feedbackQueries";
import { useFileUploadContext } from "@/lib/fileQueries";

/**
 * Master v5 Prompt 12 — a single FeedbackRequest. The coach's entry
 * point into drafting Feedback that fulfills it (owner/evidence are
 * always derived server-side from THIS request, never re-typed here).
 */
export default function FeedbackRequestDetail() {
  const { workspaceId, frqId } = useParams();
  const navigate = useNavigate();
  const { data, isPending } = useFeedbackRequests(workspaceId);
  const { data: uploadContext } = useFileUploadContext(workspaceId);
  const cancelRequest = useCancelFeedbackRequest(workspaceId);
  const createDraft = useCreateFeedbackDraft(workspaceId);

  const request = useMemo(() => (data?.items || []).find((r) => r.frq_id === frqId), [data, frqId]);
  const isCoach = uploadContext?.workspace_role === "coach";
  const [focus, setFocus] = useState("");
  const [observation, setObservation] = useState("");

  if (isPending) {
    return <ClientShell><div className="flex items-center gap-2 text-ink-soft"><Loader2 size={16} className="animate-spin" /> Loading…</div></ClientShell>;
  }
  if (!request) return <ClientShell><p className="text-ink-soft text-sm" data-testid="feedback-request-not-found">This request wasn't found.</p></ClientShell>;

  async function doCancel() {
    try {
      await cancelRequest.mutateAsync(frqId);
      toast.success("Request cancelled");
      navigate(`/rehearsal-room/w/${workspaceId}/feedback`);
    } catch (e) {
      toast.error(e.body?.detail || "Couldn't cancel this request");
    }
  }

  async function draftFromRequest() {
    try {
      const draft = await createDraft.mutateAsync({
        request_id: frqId, focus: focus.trim(), priorities: [{ observation: observation.trim() }],
      });
      navigate(`/rehearsal-room/w/${workspaceId}/feedback/${draft.fbk_id}`);
    } catch (e) {
      toast.error(e.body?.detail || "Couldn't start a draft");
    }
  }

  return (
    <ClientShell>
      <div className="max-w-2xl" data-testid="feedback-request-detail-page">
        <button onClick={() => navigate(`/rehearsal-room/w/${workspaceId}/feedback`)} className="flex items-center gap-1.5 text-sm text-ink-soft hover:text-ink mb-4" data-testid="feedback-request-back-btn">
          <ArrowLeft size={14} /> Back to Feedback
        </button>
        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid="feedback-request-status">{request.status}</span>
        <h1 className="font-serif text-2xl text-ink mt-2 mb-3">{request.question_text}</h1>
        <p className="text-sm text-ink-soft mb-6">About: <span className="font-medium text-ink">{request.evidence.label || request.evidence.target_type}</span></p>

        {request.status === "open" && !isCoach && (
          <Button variant="outline" onClick={doCancel} disabled={cancelRequest.isPending} data-testid="feedback-request-cancel-btn">
            <XCircle size={14} className="mr-1.5" /> Cancel request
          </Button>
        )}
        {request.status === "open" && isCoach && (
          <div className="space-y-3 rounded-xl border border-ink/10 bg-white/70 p-4" data-testid="feedback-request-draft-starter">
            <p className="text-sm font-medium text-ink">Start your feedback</p>
            <Input value={focus} onChange={(e) => setFocus(e.target.value)} placeholder="One focus for this feedback" data-testid="feedback-request-focus-input" />
            <Textarea value={observation} onChange={(e) => setObservation(e.target.value)} placeholder="Your first observation" rows={3} data-testid="feedback-request-observation-input" />
            <Button onClick={draftFromRequest} disabled={createDraft.isPending || !focus.trim() || !observation.trim()} data-testid="feedback-request-draft-btn">
              <ClipboardCheck size={14} className="mr-1.5" /> Start Draft
            </Button>
          </div>
        )}
        {request.status === "fulfilled" && (
          <Button variant="outline" onClick={() => navigate(`/rehearsal-room/w/${workspaceId}/feedback/${request.fulfilled_by_fbk_id}`)} data-testid="feedback-request-view-fulfillment-btn">
            View the feedback
          </Button>
        )}
      </div>
    </ClientShell>
  );
}
